'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { type LoginSchema, loginSchema } from '@labi-api-libs/schemas'
import { AlertCircle, LockKeyhole, User } from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  useToast,
} from '@/components/ui/'
import { useForm } from '@/hooks/useForm'
import { generateRoutePath } from '@/utils/routes/generateRoutePath'

import { login } from './actions/login'

export default function LoginRegistration() {
  const router = useRouter()

  const form = useForm<LoginSchema>({
    defaultValues: {
      registration: '',
      password: '',
    },
    resolver: zodResolver(loginSchema),
  })
  const { handleSubmit } = form

  const { toast } = useToast()

  const action: () => void = handleSubmit(async (data) => {
    const { message, success, data: user } = await login(data)

    if (!success) {
      toast({
        title: 'Algo deu errado',
        description: message,
        variant: 'destructive',
        icon: AlertCircle,
      })
      return
    }

    if (user?.passwordChangeRequired) {
      router.push(generateRoutePath('changePassword'))

      return
    }

    router.push(generateRoutePath('home'))
  })

  return (
    <Form {...form}>
      <form className="flex w-full flex-col items-center gap-4" action={action}>
        <FormField
          control={form.control}
          name="registration"
          render={({ field, fieldState }) => (
            <FormItem className="w-full">
              <FormLabel>Matrícula</FormLabel>
              <FormControl>
                <Input
                  placeholder="Matrícula"
                  variant={fieldState.error && 'error'}
                  icons={[
                    {
                      component: User,
                      position: 'start',
                    },
                  ]}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field, fieldState }) => (
            <FormItem className="w-full">
              <FormLabel>Senha</FormLabel>
              <FormControl>
                <Input
                  placeholder="Senha"
                  variant={fieldState.error && 'error'}
                  type="password"
                  icons={[
                    {
                      component: LockKeyhole,
                      position: 'start',
                    },
                  ]}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-full gap-2"
          variant="accent"
          disabled={form.buttonDisabled}
        >
          Entrar
        </Button>
        <Button type="button" variant="link" className="self-start" asChild>
          <Link href={generateRoutePath('resetPassword')}>
            Esqueceu sua senha?
          </Link>
        </Button>
      </form>
    </Form>
  )
}
